// composer/EmojiPicker.tsx — lightweight emoji popover for the composer.
//
// A curated grid (8 categories × common emoji, native unicode). Arrow-key
// navigable via a roving tabindex; Escape/click-outside close (handled by the
// shared Popover). Selecting an emoji calls onSelect(char) and closes.

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import { Popover } from '@/ui';
import { ComposerTool as IconButton } from './ComposerTool';
import { EMOJI_CATEGORIES } from './emojiData';

const Panel = styled.div`
  width: 260px;
  max-height: 260px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CategoryLabel = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 2px;
`;

const EmojiBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  padding: 6px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.color.glassBorder};
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

const ALL_EMOJI = EMOJI_CATEGORIES.flatMap((c) => c.emoji);

// Precomputed once at module load (categories are static) — each category's starting
// offset into the flat ALL_EMOJI list, so render never mutates a running index.
const CATEGORY_OFFSETS: number[] = (() => {
  let offset = 0;
  return EMOJI_CATEGORIES.map((cat) => {
    const start = offset;
    offset += cat.emoji.length;
    return start;
  });
})();

export interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

export function EmojiPicker({ onSelect }: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Move focus into the grid (the first emoji) whenever the popover opens — a
  // keyboard user opening it via Enter/Space would otherwise land nowhere. Only
  // the imperative focus lives in the effect (no setState there); the roving-
  // tabindex reset happens in handleOpenChange, alongside the open-state change
  // that triggers it.
  useEffect(() => {
    if (!open) return;
    btnRefs.current[0]?.focus();
  }, [open]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) setActiveIndex(0);
  }

  function handlePick(emoji: string) {
    onSelect(emoji);
    setOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const cols = 6;
    let next: number;
    if (e.key === 'ArrowRight') next = Math.min(ALL_EMOJI.length - 1, activeIndex + 1);
    else if (e.key === 'ArrowLeft') next = Math.max(0, activeIndex - 1);
    else if (e.key === 'ArrowDown') next = Math.min(ALL_EMOJI.length - 1, activeIndex + cols);
    else if (e.key === 'ArrowUp') next = Math.max(0, activeIndex - cols);
    else return;
    e.preventDefault();
    setActiveIndex(next);
    btnRefs.current[next]?.focus();
  }

  return (
    <Popover
      trigger={<IconButton icon="smile" label="Add emoji" size={18} />}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Panel role="group" aria-label="Emoji picker" onKeyDown={handleKeyDown}>
        {EMOJI_CATEGORIES.map((cat, catIndex) => (
          <div key={cat.label}>
            <CategoryLabel>{cat.label}</CategoryLabel>
            <Grid>
              {cat.emoji.map((emoji, emojiIndex) => {
                const idx = CATEGORY_OFFSETS[catIndex]! + emojiIndex;
                return (
                  <EmojiBtn
                    key={emoji}
                    type="button"
                    tabIndex={idx === activeIndex ? 0 : -1}
                    aria-label={`Insert ${emoji}`}
                    ref={(el) => {
                      btnRefs.current[idx] = el;
                    }}
                    onFocus={() => setActiveIndex(idx)}
                    onClick={() => handlePick(emoji)}
                  >
                    {emoji}
                  </EmojiBtn>
                );
              })}
            </Grid>
          </div>
        ))}
      </Panel>
    </Popover>
  );
}
